/** STORY-111: find a room host on the same Wi-Fi without typing its address.
 *
 * The Teacher Computer app answers `GET /v1/host/info` on port 7420 without credentials
 * and with an open CORS header, so a phone on the same network can probe its subnet. The
 * subnet comes from the Lan plugin when the shell has it; a browser or a shell without it
 * scans the ranges home routers and hotspots hand out. A network that keeps clients apart
 * (client isolation) finds nothing: the hotspot or the typed address remains.
 */
import { capabilities } from "./capabilities";

export type Room = { origin: string; room: string; device_id: string; addresses: string[] };

export const HOST_PORT = 7420;
const DEFAULT_SUBNETS = ["192.168.1", "192.168.0", "10.0.0", "172.20.10", "192.168.43", "192.168.137", "192.168.8", "192.168.2"];

function subnetOf(ip: string): string | null {
  const m = /^(\d+\.\d+\.\d+)\.\d+$/.exec(ip);
  return m ? m[1]! : null;
}

/** /24 prefixes to scan, most likely first, without duplicates. */
export async function candidateSubnets(lastServer: string | null): Promise<string[]> {
  const out: string[] = [];
  const add = (s: string | null) => {
    if (s && !out.includes(s)) out.push(s);
  };
  if (lastServer) {
    try {
      add(subnetOf(new URL(lastServer).hostname));
    } catch {
      // not an address
    }
  }
  let own = 0;
  if (capabilities.lan) {
    try {
      const { addresses } = await capabilities.lan.addresses();
      for (const a of addresses) {
        if (a.prefix >= 16) {
          add(subnetOf(a.ip));
          own += 1;
        }
      }
    } catch {
      // the plugin failed: fall through to the defaults
    }
  }
  if (own === 0) DEFAULT_SUBNETS.forEach(add);
  return out;
}

async function probe(ip: string, timeoutMs: number): Promise<Room | null> {
  const origin = `http://${ip}:${HOST_PORT}`;
  try {
    const r = await fetch(`${origin}/v1/host/info`, { signal: AbortSignal.timeout(timeoutMs) });
    if (!r.ok) return null;
    const body = (await r.json()) as { local?: boolean; room?: string; device_id?: string; addresses?: string[] };
    if (body.local !== true || !body.device_id) return null;
    return { origin, room: body.room ?? ip, device_id: body.device_id, addresses: body.addresses ?? [] };
  } catch {
    return null;
  }
}

/** Scan the subnets one after another (most likely first) and stop at the first one that
 * answers; `onProgress` gets 0..1 over everything queued. A /24 takes a few seconds. */
export async function findRooms(
  subnets: string[],
  opts: { concurrency?: number; timeoutMs?: number; limit?: number; onProgress?: (p: number) => void } = {},
): Promise<Room[]> {
  const { concurrency = 64, timeoutMs = 900, limit = 8, onProgress } = opts;
  const found = new Map<string, Room>();
  const total = subnets.length * 254;
  let done = 0;
  for (const subnet of subnets) {
    const targets = Array.from({ length: 254 }, (_, i) => `${subnet}.${i + 1}`);
    let next = 0;
    async function worker() {
      while (next < targets.length && found.size < limit) {
        const ip = targets[next++]!;
        const room = await probe(ip, timeoutMs);
        if (room && !found.has(room.device_id)) found.set(room.device_id, room);
        done += 1;
        onProgress?.(done / total);
      }
    }
    await Promise.all(Array.from({ length: Math.min(concurrency, targets.length) }, worker));
    if (found.size > 0) break;
  }
  onProgress?.(1);
  return [...found.values()];
}
